import Component from '@glimmer/component';
import { service } from '@ember/service';
import { LinkTo } from '@ember/routing';
import Chip from 'tio-reimagined/components/chip';
import { PhClock, PhCheckCircle, PhWarningCircle, PhXCircle } from 'ember-phosphor-icons';
import {
  APPLICATION_STATUS_MAP,
  DEFAULT_STATUS,
  type StatusInfo,
} from 'tio-employee/utils/tas/applicationStateMap';
import type { IntlService } from 'ember-intl';
import type {
  RecentApplication,
  ProgramInstance,
} from 'tio-common/utils/tuition-assistance/application-states';

interface InstanceActivityGroupSignature {
  Args: {
    instance: ProgramInstance;
    onAddApplication: () => void;
  };
  Element: HTMLDivElement;
}

interface ApplicationItemSignature {
  Args: {
    application: RecentApplication;
  };
  Element: HTMLAnchorElement;
}

/**
 * ApplicationItem - Individual application row component
 */
class ApplicationItem extends Component<ApplicationItemSignature> {
  @service declare intl: IntlService;

  get statusInfo(): StatusInfo {
    const app = this.args.application;

    // If there's a refund amount due, show "In Repayment" regardless of state
    if (app.paymentRefundRequestedAmount && app.paymentRefundRequestedAmount > 0) {
      return { label: 'In Repayment', intent: 'danger', icon: 'warning' };
    }

    const state = app.tasApplicationStateKeyword || '';
    return APPLICATION_STATUS_MAP[state] || DEFAULT_STATUS;
  }

  get isClockIcon() {
    return this.statusInfo.icon === 'clock';
  }

  get isCheckIcon() {
    return this.statusInfo.icon === 'check';
  }

  get isXIcon() {
    return this.statusInfo.icon === 'x';
  }

  get isWarningIcon() {
    return this.statusInfo.icon === 'warning';
  }

  get applicationId() {
    return this.args.application.tasApplicationIdKeyword || this.args.application.id;
  }

  get applicationName() {
    const app = this.args.application;
    const type = app.tasProgramTemplateTypeKeyword;

    // For Type 4, use application name field
    if (type === 'TAS.ProgramType.4.2' || type === 'TAS.ProgramType.4.3') {
      return app.tasApplicationFieldApplicationNameKeyword || 'Application';
    }

    // For Type 2/3, check application name field first
    return (
      app.tasApplicationFieldApplicationNameKeyword ||
      app.tasProgramInstanceFieldProgramSpecializationKeyword ||
      'Application'
    );
  }

  get programName() {
    return this.args.application.tasProgramTemplateFieldProgramNameKeyword || 'Unknown Program';
  }

  get formattedCreatedDate() {
    const app = this.args.application;
    // Use application created date, then instance created date, then general createdAt
    const dateStr =
      app.tasApplicationCreatedAtDate || app.tasProgramInstanceCreatedAtDate || app.createdAt;
    if (!dateStr) return '';

    const date = new Date(dateStr);
    const formatted = this.intl.formatDate(date, {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
    });
    return `created ${formatted}`;
  }

  get hasAmount() {
    const app = this.args.application;
    return !!(
      app.paymentPendingAmount ||
      app.paymentFinalApprovedTotalAmount ||
      app.paymentPaidAmount ||
      app.tasApplicationFieldPreApprovedTotalInteger ||
      app.tasApplicationFieldCardPreApprovedTotalInteger
    );
  }

  /**
   * Calculate payment status for Type 4 applications
   * Includes both tuition (PRE_APPROVED_TOTAL) and course cash (CARD_PRE_APPROVED_TOTAL)
   */
  get paymentInfo() {
    const app = this.args.application;

    // Check if in a "requested" state (not yet approved)
    const applicationState = app.tasApplicationStateKeyword;
    const isRequestedState =
      applicationState === 'TAS.ApplicationState.CREATED' ||
      applicationState === 'TAS.ApplicationState.DEFINE_COURSES';

    // Paid takes priority
    if (app.paymentPaidAmount && app.paymentPaidAmount > 0) {
      return { amount: app.paymentPaidAmount, label: 'paid', colorClass: 'text-green-600' };
    }

    // Type 4 application amounts (in cents) - tuition + course cash
    const tuitionAmount = isRequestedState
      ? (app.tasApplicationFieldPreApprovedTotalInteger ?? 0)
      : (app.tasApplicationFieldFinalApprovedTotalInteger ??
        app.tasApplicationFieldPreApprovedTotalInteger ??
        0);
    const courseCashAmount = isRequestedState
      ? (app.tasApplicationFieldCardPreApprovedTotalInteger ?? 0)
      : (app.tasApplicationFieldCardFinalApprovedTotalInteger ??
        app.tasApplicationFieldCardPreApprovedTotalInteger ??
        0);
    const totalTasAmount = tuitionAmount + courseCashAmount;

    if (totalTasAmount > 0) {
      return {
        amount: totalTasAmount,
        label: isRequestedState ? 'requested' : 'approved',
        colorClass: 'text-gray-400',
      };
    }

    // Legacy payment fields (also in cents)
    if (app.paymentFinalApprovedTotalAmount && app.paymentFinalApprovedTotalAmount > 0) {
      return {
        amount: app.paymentFinalApprovedTotalAmount,
        label: 'approved',
        colorClass: 'text-green-600',
      };
    }
    if (app.paymentPendingAmount && app.paymentPendingAmount > 0) {
      return { amount: app.paymentPendingAmount, label: 'requested', colorClass: 'text-gray-400' };
    }

    return { amount: 0, label: '', colorClass: 'text-gray-400' };
  }

  get formattedAmount() {
    // Amounts are stored in cents, convert to dollars
    const amountInDollars = this.paymentInfo.amount / 100;
    return this.intl.formatNumber(amountInDollars, { style: 'currency', currency: 'USD' });
  }

  get amountLabel() {
    return this.paymentInfo.label;
  }

  get amountColorClass() {
    return this.paymentInfo.colorClass;
  }

  <template>
    <LinkTo
      @route="authenticated.tas.applications.show"
      @model={{this.applicationId}}
      class="border border-gray-200 rounded-xl p-4 flex items-center gap-4 hover:bg-gray-50 transition-colors"
      ...attributes
    >
      {{! Left: Status Icon }}
      <div
        class="{{if this.isWarningIcon 'bg-red-100' 'bg-gray-100'}}
          {{if this.isCheckIcon 'bg-teal-100' ''}}
          rounded-full p-0.5"
      >
        {{#if this.isClockIcon}}
          <PhClock @size={{20}} class="text-gray-500" />
        {{else if this.isCheckIcon}}
          <PhCheckCircle @size={{20}} class="text-green-600" />
        {{else if this.isXIcon}}
          <PhXCircle @size={{20}} class="text-gray-500" />
        {{else if this.isWarningIcon}}
          <PhWarningCircle @size={{20}} class="text-red-600" />
        {{/if}}
      </div>

      {{! Middle: Application Info }}
      <div class="flex-1 flex flex-col gap-1.5">
        {{! Row 1: Application Name + Status Chip }}
        <div class="flex items-center gap-2">
          <span class="font-medium text-gray-900 text-base">
            {{this.applicationName}}
          </span>
          <Chip @intent={{this.statusInfo.intent}} @appearance="faded" @size="sm">
            {{this.statusInfo.label}}
          </Chip>
        </div>

        {{! Row 2: Program Name • Created Date }}
        <div class="flex items-center gap-1.5 text-sm text-gray-400 tracking-wide">
          <span>{{this.programName}}</span>
          <span class="text-gray-300">•</span>
          <span>{{this.formattedCreatedDate}}</span>
        </div>
      </div>

      {{! Right: Amount }}
      {{#if this.hasAmount}}
        <div class="flex flex-col items-end gap-1 w-24">
          <span class="font-semibold text-base {{this.amountColorClass}}">
            {{this.formattedAmount}}
          </span>
          <span class="text-xs text-gray-400 tracking-wide">
            {{this.amountLabel}}
          </span>
        </div>
      {{else}}
        <div class="w-24 flex justify-end">
          <span class="text-gray-300 font-semibold">---</span>
        </div>
      {{/if}}
    </LinkTo>
  </template>
}

/**
 * InstanceActivityGroup Component
 *
 * Displays a program instance with its header and child applications
 * in a Figma-matching layout for the getting-started page.
 */
export default class InstanceActivityGroup extends Component<InstanceActivityGroupSignature> {
  @service declare intl: IntlService;

  get instanceName() {
    const app = this.args.instance.instanceApplication;
    // e.g., "Annual Professional Seminars 2026"
    return (
      app.tasProgramInstanceFieldProgramSpecializationKeyword ||
      app.tasProgramInstanceFieldSchoolInstitutionNameKeyword ||
      this.formatInstanceName(app)
    );
  }

  get instanceId() {
    const app = this.args.instance.instanceApplication;
    return app.tasProgramInstanceIdKeyword || app.tasProgramInstanceId || '';
  }

  formatInstanceName(app: RecentApplication): string {
    const templateName = app.tasProgramTemplateFieldProgramNameKeyword || 'Program';
    const dateStr = app.tasProgramInstanceCreatedAtDate;
    if (!dateStr) return templateName;

    const date = new Date(dateStr);
    const year = date.getFullYear();
    return `${templateName} ${year}`;
  }

  get allApplications() {
    const instance = this.args.instance;
    // Child applications only (instance header is separate)
    return instance.childApplications;
  }

  <template>
    <div class="flex flex-col gap-4" ...attributes>
      <div class="flex items-center justify-between">
        <LinkTo
          @route="authenticated.tas.programs.show"
          @model={{this.instanceId}}
          class="font-semibold text-gray-500 text-base hover:text-ocean-600 hover:underline transition-colors"
        >
          {{this.instanceName}}
        </LinkTo>
        {{!-- <button
          type="button"
          class="text-ocean-600 font-medium text-base underline hover:text-ocean-800"
          {{on "click" @onAddApplication}}
        >
          {{t "tas.getting_started.add_application"}}
        </button> --}}
      </div>

      {{! Application Items }}
      <div class="flex flex-col gap-3">
        {{#each this.allApplications as |app|}}
          <ApplicationItem @application={{app}} />
        {{/each}}
      </div>
    </div>
  </template>
}
